import type { LiveComposerEvent, LiveComposerPedalEvent } from '../../CreateMusicXml';
import { cloneLiveComposerEvents } from './snapshot';

export type PassageRange = { startDivisions: number; endDivisions: number };
export type Passage = {
  events: LiveComposerEvent[];
  pedalEvents: LiveComposerPedalEvent[];
  spanDivisions: number;
};
export type PasteMode = 'insert' | 'replace' | 'merge';
const startOf = (event: LiveComposerEvent) => event.startDivisions ?? 0;
const endOf = (event: LiveComposerEvent) => startOf(event) + event.durationDivisions;
const pedalAt = (pedals: LiveComposerPedalEvent[], position: number) =>
  [...pedals]
    .sort((a, b) => a.positionDivisions - b.positionDivisions)
    .filter((event) => event.positionDivisions <= position)
    .at(-1);

/** Silence belongs to the copied span. Notes crossing its edges are clipped in the copy. */
export function copyPassage(
  events: LiveComposerEvent[],
  pedals: LiveComposerPedalEvent[],
  range: PassageRange,
): Passage {
  if (range.endDivisions <= range.startDivisions)
    throw new Error('Select a passage with a positive duration.');
  const spanDivisions = range.endDivisions - range.startDivisions;
  const copied = cloneLiveComposerEvents(
    events.filter(
      (event) => startOf(event) < range.endDivisions && endOf(event) > range.startDivisions,
    ),
  );
  return {
    spanDivisions,
    events: copied.map((event) => ({
      ...event,
      startDivisions: Math.max(startOf(event), range.startDivisions) - range.startDivisions,
      durationDivisions:
        Math.min(endOf(event), range.endDivisions) - Math.max(startOf(event), range.startDivisions),
      restBeforeDivisions: 0,
      adaptiveTiming: false,
      ...(startOf(event) < range.startDivisions
        ? { slurStart: undefined, slurStop: undefined }
        : {}),
      ...(endOf(event) > range.endDivisions ? { slurStop: undefined } : {}),
    })),
    pedalEvents: [
      {
        id: 'passage:pedal-start',
        source: 'midi',
        capturedAtMs: 0,
        pedalDown: pedalAt(pedals, range.startDivisions)?.pedalDown ?? false,
        value: pedalAt(pedals, range.startDivisions)?.value ?? 0,
        positionDivisions: 0,
      },
      ...pedals
        .filter(
          (event) =>
            event.positionDivisions > range.startDivisions &&
            event.positionDivisions < range.endDivisions,
        )
        .map((event) => ({
          ...event,
          positionDivisions: event.positionDivisions - range.startDivisions,
          adaptiveTiming: false,
        })),
    ],
  };
}

/** Replace splits crossing material and preserves both outside portions. Insert shifts both hands. */
export function pastePassage(
  events: LiveComposerEvent[],
  pedals: LiveComposerPedalEvent[],
  passage: Passage,
  position: number,
  mode: PasteMode,
  makeId: () => string,
) {
  const end = position + passage.spanDivisions;
  if (!Number.isFinite(position) || position < 0 || !Number.isFinite(end))
    throw new Error('Choose a valid paste position.');
  const additions = passage.events.map((event) => ({
    ...event,
    id: makeId(),
    copiedFromId: event.id,
    source: 'manual' as const,
    startDivisions: position + startOf(event),
    adaptiveTiming: false,
  }));
  if (mode === 'merge') {
    const conflict = additions.some((added) =>
      events.some((event) => {
        const staff = event.staff ?? ((event.midiNotes[0] ?? 60) >= 60 ? 1 : 2);
        const addedStaff = added.staff ?? ((added.midiNotes[0] ?? 60) >= 60 ? 1 : 2);
        return (
          staff === addedStaff &&
          startOf(event) < endOf(added) &&
          endOf(event) > startOf(added) &&
          (startOf(event) !== startOf(added) || event.durationDivisions !== added.durationDivisions)
        );
      }),
    );
    if (conflict)
      throw new Error(
        'These rhythms overlap in the same hand. Choose Insert or Replace to keep the passage clear.',
      );
  }
  const retained = events.flatMap((event): LiveComposerEvent[] => {
    if (mode === 'merge') return [event];
    if (mode === 'insert') {
      if (startOf(event) >= position)
        return [
          {
            ...event,
            adaptiveTiming: false,
            startDivisions: startOf(event) + passage.spanDivisions,
          },
        ];
      if (endOf(event) <= position) return [event];
      return [
        { ...event, adaptiveTiming: false, durationDivisions: position - startOf(event) },
        {
          ...event,
          id: makeId(),
          adaptiveTiming: false,
          startDivisions: end,
          durationDivisions: endOf(event) - position,
        },
      ];
    }
    if (endOf(event) <= position || startOf(event) >= end) return [event];
    const fragments: LiveComposerEvent[] = [];
    if (startOf(event) < position)
      fragments.push({
        ...event,
        adaptiveTiming: false,
        durationDivisions: position - startOf(event),
        slurStop: undefined,
      });
    if (endOf(event) > end)
      fragments.push({
        ...event,
        id: fragments.length ? makeId() : event.id,
        adaptiveTiming: false,
        startDivisions: end,
        durationDivisions: endOf(event) - end,
        slurStart: undefined,
      });
    return fragments;
  });
  const destinationPedal = pedalAt(pedals, mode === 'insert' ? position : end);
  const retainedPedals = pedals.flatMap((event) => {
    if (mode === 'replace' && event.positionDivisions >= position && event.positionDivisions < end)
      return [];
    return [
      {
        ...event,
        positionDivisions:
          event.positionDivisions +
          (mode === 'insert' && event.positionDivisions >= position ? passage.spanDivisions : 0),
        adaptiveTiming: false,
      },
    ];
  });
  const copiedPedals =
    mode === 'merge'
      ? []
      : [
          ...passage.pedalEvents.map((event) => ({
            ...event,
            id: makeId(),
            positionDivisions: event.positionDivisions + position,
            adaptiveTiming: false,
          })),
          {
            id: makeId(),
            source: 'midi' as const,
            capturedAtMs: 0,
            positionDivisions: end,
            pedalDown: destinationPedal?.pedalDown ?? false,
            value: destinationPedal?.value ?? 0,
          },
        ];
  return {
    events: [...retained, ...additions].sort((a, b) => startOf(a) - startOf(b)),
    pedalEvents: [...retainedPedals, ...copiedPedals].sort(
      (a, b) => a.positionDivisions - b.positionDivisions,
    ),
    selectedIds: additions.map((event) => event.id),
    range: { startDivisions: position, endDivisions: end },
  };
}

const isPitched = (event: LiveComposerEvent) => event.midiNotes.length > 0;
const byStart = (a: LiveComposerEvent, b: LiveComposerEvent) => startOf(a) - startOf(b);
const inRange = (event: LiveComposerEvent, range: PassageRange) =>
  startOf(event) >= range.startDivisions && startOf(event) < range.endDivisions;

export type PitchPasteResult = {
  events: LiveComposerEvent[];
  changedIds: string[];
  range: PassageRange;
};

/**
 * Keeps the selection's rhythm and replaces its pitches with the clipboard's, in
 * order. The clipboard's pitch sequence repeats when the selection has more notes
 * than it, and its surplus is ignored when the selection has fewer; rests in the
 * selection are untouched because they carry rhythm, not pitch.
 */
export function pastePitches(
  events: LiveComposerEvent[],
  passage: Passage,
  range: PassageRange,
): PitchPasteResult {
  if (range.endDivisions <= range.startDivisions)
    throw new Error('Select the passage whose pitches should change.');
  const sourcePitches = passage.events
    .filter(isPitched)
    .sort(byStart)
    .map((event) => ({
      midiNotes: [...event.midiNotes],
      accidentalSpellings: event.accidentalSpellings ? { ...event.accidentalSpellings } : undefined,
    }));
  if (sourcePitches.length === 0)
    throw new Error('Copy some notes first; the clipboard holds no pitches.');
  const targets = events.filter((event) => isPitched(event) && inRange(event, range)).sort(byStart);
  if (targets.length === 0) throw new Error('The selected passage has no notes to re-pitch.');
  const replacements = new Map(
    targets.map((event, index) => {
      const source = sourcePitches[index % sourcePitches.length];
      // The observed pitch is gone, so the evidence that described it must not follow
      // the new one; velocity and provenance belong to the note that was played.
      const {
        velocities: _velocities,
        performanceNoteIds: _performanceNoteIds,
        rawStartedAtMs: _rawStartedAtMs,
        rawEndedAtMs: _rawEndedAtMs,
        rawCaptureSessionStartedAtMs: _rawSessionStart,
        rawCaptureSessionStartDivisions: _rawSessionDivisions,
        ...rest
      } = event;
      return [
        event.id,
        {
          ...rest,
          midiNotes: [...source.midiNotes],
          accidentalSpellings: source.accidentalSpellings,
          source: 'manual' as const,
          adaptiveTiming: false,
        },
      ];
    }),
  );
  return {
    events: events.map((event) => replacements.get(event.id) ?? event),
    changedIds: targets.map((event) => event.id),
    range,
  };
}

export type RhythmPasteResult = {
  events: LiveComposerEvent[];
  selectedIds: string[];
  range: PassageRange;
  /** Pitches that did not fit inside the selection once re-timed. */
  droppedPitchCount: number;
};

/**
 * Keeps the selection's pitches and re-times them onto the clipboard's rhythm. The
 * rhythm repeats bar after bar within the selection, so a one-bar pattern can be
 * applied across four bars. Pitches that would spill past the selection are dropped
 * and counted rather than pushed into the music that follows.
 */
export function pasteRhythm(
  events: LiveComposerEvent[],
  passage: Passage,
  range: PassageRange,
  makeId: () => string,
): RhythmPasteResult {
  if (range.endDivisions <= range.startDivisions)
    throw new Error('Select the passage whose rhythm should change.');
  const slots = passage.events
    .filter(isPitched)
    .sort(byStart)
    .map((event) => ({ start: startOf(event), duration: event.durationDivisions }));
  if (slots.length === 0 || passage.spanDivisions <= 0)
    throw new Error('Copy a rhythm first; the clipboard holds no notes.');
  const pitched = events.filter((event) => isPitched(event) && inRange(event, range)).sort(byStart);
  if (pitched.length === 0) throw new Error('The selected passage has no notes to re-time.');

  const retimed: LiveComposerEvent[] = [];
  let droppedPitchCount = 0;
  pitched.forEach((event, index) => {
    const slot = slots[index % slots.length];
    const cycle = Math.floor(index / slots.length);
    const start = range.startDivisions + cycle * passage.spanDivisions + slot.start;
    if (start + slot.duration > range.endDivisions) {
      droppedPitchCount += 1;
      return;
    }
    // Pitch, hand and touch survive; the timing evidence and slurs describe the old
    // placement and are left behind.
    const {
      rawStartedAtMs: _rawStartedAtMs,
      rawEndedAtMs: _rawEndedAtMs,
      rawCaptureSessionStartedAtMs: _rawSessionStart,
      rawCaptureSessionStartDivisions: _rawSessionDivisions,
      slurStart: _slurStart,
      slurStop: _slurStop,
      ...rest
    } = event;
    retimed.push({
      ...rest,
      id: makeId(),
      copiedFromId: event.id,
      source: 'manual',
      startDivisions: start,
      durationDivisions: slot.duration,
      restBeforeDivisions: 0,
      adaptiveTiming: false,
    });
  });
  const untouched = events.filter((event) => !inRange(event, range));
  return {
    events: [...untouched, ...retimed].sort(byStart),
    selectedIds: retimed.map((event) => event.id),
    range,
    droppedPitchCount,
  };
}
